/**
 * Comparte nota de voz .mp3 vía Web Share API (o descarga + clipboard).
 */
import {
  blobToFile,
  shareOrFallback,
} from '../utils/sharePayload'
import { VOICE_NOTE_FILENAME } from './types'
import { buildVoiceNoteShareText } from './voiceNoteShareText'

/**
 * @param {string} [text]
 * @returns {Promise<boolean>}
 */
async function copyTextToClipboard(text) {
  if (!text) return false
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return !!ok
  } catch (e) {
    return false
  }
}

/**
 * @param {{
 *   mp3Blob: Blob|File,
 *   studyUrl?: string,
 *   filename?: string,
 *   title?: string,
 *   text?: string,
 *   patientName?: string,
 *   patientId?: string,
 *   studyDate?: string,
 *   studyDescription?: string,
 *   modality?: string,
 *   createdAt?: number,
 *   transcriptText?: string,
 *   transcript?: { text?: string },
 * }} opts
 * @returns {Promise<{ ok: boolean, mode: string, clipboard?: boolean, error?: any }>}
 */
export async function shareVoiceNote(opts) {
  const blob = opts && opts.mp3Blob
  if (!blob) {
    return { ok: false, mode: 'unavailable', error: new Error('Sin audio') }
  }
  const filename = (opts && opts.filename) || VOICE_NOTE_FILENAME
  const studyUrl =
    (opts && opts.studyUrl) ||
    (typeof window !== 'undefined' ? window.location.href : '')
  const title = (opts && opts.title) || 'Nota médica UDV'
  const text =
    (opts && opts.text) ||
    buildVoiceNoteShareText({
      patientName: opts && opts.patientName,
      patientId: opts && opts.patientId,
      studyDate: opts && opts.studyDate,
      studyDescription: opts && opts.studyDescription,
      modality: opts && opts.modality,
      noteName: filename,
      createdAt: opts && opts.createdAt,
      studyUrl,
      transcriptText: opts && opts.transcriptText,
      transcript: opts && opts.transcript,
    })

  const file = blobToFile(blob, filename, 'audio/mpeg')
  const result = await shareOrFallback({
    files: [file],
    title,
    text,
    url: studyUrl || undefined,
  })

  // Muchos targets de Web Share ignoran `text` si hay `files` → portapapeles
  const clipboard =
    result.mode === 'abort' ? false : await copyTextToClipboard(text)

  return {
    ...result,
    clipboard,
  }
}

export default shareVoiceNote
